import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { withLocalize } from 'react-localize-redux';
import { useFormikContext } from 'formik-2';
import { Toggle } from '../../../components/widgets';
import Select from '../../../components/widgets/Select';
import Checkbox from '../../../components/atoms/Checkbox';
import { ConfirmChangesDialog } from '../../../components/organisms/NavigationBlocker';
import FormSection from '../FormSection';
import FormField from '../FormField';
import FormInnerSection from '../FormInnerSection';
import RequiredInput from '../RequiredInput';
import {
    ATTRIBUTION_TAB_IDS,
    CAMPAIGN_OBJECTIVE_OPTIONS,
    DATA_SCOPE_EVENTS_OPTIONS,
    DATA_SCOPE_INSTALLS_OPTIONS,
    PLATFORM_OPTIONS,
    PLATFORMS,
    PLATFORM_TEMPLATE_OPTIONS,
    TRANSLATE_PREFIX,
} from '../../consts';
import css from './GeneralTab.css';

const TAB_PREFIX = `${TRANSLATE_PREFIX}.GENERAL_TAB`;

const GeneralTab = ({ translate, readOnly = false }) => {
    const { values, setFieldValue, setValues } = useFormikContext();
    const [showDisablePostbacksDialog, setShowDisablePostbacksDialog] = useState(false);

    const generalData = values.formData?.[ATTRIBUTION_TAB_IDS.GENERAL] || {};

    const templateOptions = PLATFORM_TEMPLATE_OPTIONS.map(opt => ({
        value: opt.value,
        label: translate(opt.labelKey),
    }));
    const selectedTemplateOption = templateOptions.find(opt => opt.value === generalData.platform_template) || null;

    const receivePostbacks = generalData.receive_postbacks ?? true;
    const selectedPlatforms = generalData.platforms || [];
    const selectedObjectives = generalData.campaign_objectives || [];
    const hasOtherPlatform = selectedPlatforms.includes(PLATFORMS.OTHER);
    const platformsOther = generalData.platforms_other || '';

    const setGeneralField = (field, value) => {
        setFieldValue(`formData.${ATTRIBUTION_TAB_IDS.GENERAL}.${field}`, value);
    };

    const hasAnyValues = obj => {
        if (obj === null || obj === undefined) return false;
        if (typeof obj !== 'object') return true;
        if (Array.isArray(obj)) return obj.length > 0;
        return Object.values(obj).some(v => hasAnyValues(v));
    };

    const clearPostbacksFields = () => {
        setGeneralField('receive_postbacks', false);
        setGeneralField('installs_data_scope', undefined);
        setGeneralField('events_data_scope', undefined);
        setFieldValue(`formData.${ATTRIBUTION_TAB_IDS.POSTBACK_OVERALL_SETUP}`, undefined);
        setFieldValue(`formData.${ATTRIBUTION_TAB_IDS.POSTBACK_TEMPLATES}`, undefined);
    };

    const handleReceivePostbacksToggle = e => {
        const { checked } = e.target;
        if (!checked) {
            const postbackData = values.formData?.[ATTRIBUTION_TAB_IDS.POSTBACK_OVERALL_SETUP];
            const templateData = values.formData?.[ATTRIBUTION_TAB_IDS.POSTBACK_TEMPLATES];
            const hasPostbacksData =
                hasAnyValues(postbackData) ||
                hasAnyValues(templateData) ||
                !!generalData.installs_data_scope ||
                !!generalData.events_data_scope;
            if (hasPostbacksData) {
                setShowDisablePostbacksDialog(true);
                return;
            }
            clearPostbacksFields();
            return;
        }
        setGeneralField('receive_postbacks', checked);
    };

    const handleConfirmDisablePostbacks = () => {
        setShowDisablePostbacksDialog(false);
        clearPostbacksFields();
    };

    const toggleArrayField = (field, value, currentArray) => {
        const next = currentArray.includes(value) ? currentArray.filter(v => v !== value) : [...currentArray, value];
        if (field === 'platforms' && value === PLATFORMS.OTHER && !next.includes(PLATFORMS.OTHER)) {
            // Update both platforms and platforms_other atomically to avoid a Formik race condition
            // where the second setFieldValue call could clobber the first call's state update.
            setValues({
                ...values,
                formData: {
                    ...values.formData,
                    [ATTRIBUTION_TAB_IDS.GENERAL]: {
                        ...values.formData?.[ATTRIBUTION_TAB_IDS.GENERAL],
                        platforms: next,
                        platforms_other: undefined,
                    },
                },
            });
        } else {
            setGeneralField(field, next);
        }
    };

    const installsOptions = DATA_SCOPE_INSTALLS_OPTIONS.map(v => ({
        value: v,
        label: translate(`${TRANSLATE_PREFIX}.DATA_SCOPE.${v.toUpperCase()}`),
    }));

    const eventsOptions = DATA_SCOPE_EVENTS_OPTIONS.map(v => ({
        value: v,
        label: translate(`${TRANSLATE_PREFIX}.DATA_SCOPE.${v.toUpperCase()}`),
    }));

    const selectedInstalls = installsOptions.find(o => o.value === generalData.installs_data_scope) || null;
    const selectedEvents = eventsOptions.find(o => o.value === generalData.events_data_scope) || null;

    return (
        <FormSection
            titleKey={`${TRANSLATE_PREFIX}.TABS.GENERAL`}
            descriptionKey={`${TAB_PREFIX}.DESCRIPTION`}
            iconName="settings"
        >
            <FormField
                labelKey={`${TAB_PREFIX}.PLATFORM_TEMPLATE_LABEL`}
                required={!readOnly}
                subtitleKey={`${TAB_PREFIX}.PLATFORM_TEMPLATE_SUBTITLE`}
            >
                <Select
                    options={templateOptions}
                    value={selectedTemplateOption}
                    onChange={opt => setGeneralField('platform_template', opt ? opt.value : '')}
                    placeholder={translate(`${TAB_PREFIX}.SELECT_PLACEHOLDER`)}
                    isSearchable={false}
                    isClearable={false}
                    isDisabled={readOnly}
                    className={css.select}
                />
            </FormField>
            {generalData.platform_template && (
                <>
                    <FormInnerSection
                        titleKey={`${TRANSLATE_PREFIX}.CAMPAIGN_TYPES.TITLE`}
                        descriptionKey={`${TRANSLATE_PREFIX}.CAMPAIGN_TYPES.DESCRIPTION`}
                    >
                        <FormField
                            labelKey={`${TRANSLATE_PREFIX}.CAMPAIGN_TYPES.PLATFORMS_LABEL`}
                            required={!readOnly}
                            subtitleKey={`${TRANSLATE_PREFIX}.CAMPAIGN_TYPES.PLATFORMS_SUBTITLE`}
                            fullWidth
                        >
                            <div className={css.checkboxGroup}>
                                {PLATFORM_OPTIONS.map(value => {
                                    const isChecked = selectedPlatforms.includes(value);
                                    return (
                                        <Checkbox
                                            key={value}
                                            id={`platform_${value}`}
                                            label={translate(
                                                `${TRANSLATE_PREFIX}.CAMPAIGN_TYPES.PLATFORM_OPTIONS.${value.toUpperCase()}`
                                            )}
                                            checked={isChecked}
                                            onChange={() => toggleArrayField('platforms', value, selectedPlatforms)}
                                            disabled={readOnly}
                                            size="small"
                                            labelClass={isChecked ? css.checkboxLabelActive : css.checkboxLabelInactive}
                                        />
                                    );
                                })}
                            </div>
                            {hasOtherPlatform && (
                                <RequiredInput
                                    value={platformsOther}
                                    placeholder={
                                        readOnly
                                            ? undefined
                                            : translate(
                                                  `${TRANSLATE_PREFIX}.CAMPAIGN_TYPES.PLATFORMS_OTHER_PLACEHOLDER`
                                              )
                                    }
                                    onChange={val => setGeneralField('platforms_other', val)}
                                    disabled={readOnly}
                                    containerClass={css.platformsOtherRow}
                                />
                            )}
                        </FormField>
                        <FormField
                            labelKey={`${TRANSLATE_PREFIX}.CAMPAIGN_TYPES.OBJECTIVES_LABEL`}
                            required={!readOnly}
                            subtitleKey={`${TRANSLATE_PREFIX}.CAMPAIGN_TYPES.OBJECTIVES_SUBTITLE`}
                            fullWidth
                        >
                            <div className={css.checkboxGroup}>
                                {CAMPAIGN_OBJECTIVE_OPTIONS.map(value => {
                                    const isChecked = selectedObjectives.includes(value);
                                    return (
                                        <Checkbox
                                            key={value}
                                            id={`objective_${value}`}
                                            label={translate(
                                                `${TRANSLATE_PREFIX}.CAMPAIGN_TYPES.OBJECTIVE_OPTIONS.${value
                                                    .toUpperCase()
                                                    .replace(/-/g, '_')}`
                                            )}
                                            checked={isChecked}
                                            onChange={() =>
                                                toggleArrayField('campaign_objectives', value, selectedObjectives)
                                            }
                                            disabled={readOnly}
                                            size="small"
                                            labelClass={isChecked ? css.checkboxLabelActive : css.checkboxLabelInactive}
                                            labelTooltip={translate(
                                                `${TRANSLATE_PREFIX}.CAMPAIGN_TYPES.OBJECTIVE_TOOLTIPS.${value
                                                    .toUpperCase()
                                                    .replace(/-/g, '_')}`
                                            )}
                                        />
                                    );
                                })}
                            </div>
                        </FormField>
                    </FormInnerSection>
                    <FormInnerSection
                        titleKey={`${TRANSLATE_PREFIX}.DATA_SCOPE.TITLE`}
                        descriptionKey={`${TRANSLATE_PREFIX}.DATA_SCOPE.DESCRIPTION`}
                    >
                        <FormField
                            labelKey={`${TRANSLATE_PREFIX}.DATA_SCOPE.RECEIVE_POSTBACKS_LABEL`}
                            subtitleKey={`${TRANSLATE_PREFIX}.DATA_SCOPE.RECEIVE_POSTBACKS_SUBTITLE`}
                            labelTooltipKey={`${TRANSLATE_PREFIX}.DATA_SCOPE.RECEIVE_POSTBACKS_TOOLTIP`}
                            className={receivePostbacks ? css.activeField : undefined}
                            horizontal
                        >
                            <Toggle
                                checked={receivePostbacks}
                                onToggle={handleReceivePostbacksToggle}
                                disabled={readOnly}
                            />
                        </FormField>
                        <ConfirmChangesDialog
                            open={showDisablePostbacksDialog}
                            title={`${TRANSLATE_PREFIX}.DATA_SCOPE.DISABLE_POSTBACKS_TITLE`}
                            text={`${TRANSLATE_PREFIX}.DATA_SCOPE.DISABLE_POSTBACKS_TEXT`}
                            onAccept={handleConfirmDisablePostbacks}
                            onReject={() => setShowDisablePostbacksDialog(false)}
                        />
                        {receivePostbacks && (
                            <>
                                <FormField
                                    labelKey={`${TRANSLATE_PREFIX}.DATA_SCOPE.INSTALLS_LABEL`}
                                    subtitleKey={`${TRANSLATE_PREFIX}.DATA_SCOPE.INSTALLS_SUBTITLE`}
                                    required={!readOnly}
                                    horizontal
                                >
                                    <Select
                                        options={installsOptions}
                                        value={selectedInstalls}
                                        onChange={opt => setGeneralField('installs_data_scope', opt ? opt.value : '')}
                                        placeholder={translate(`${TAB_PREFIX}.SELECT_PLACEHOLDER`)}
                                        isSearchable={false}
                                        isClearable={false}
                                        isDisabled={readOnly}
                                        className={css.select}
                                    />
                                </FormField>
                                <FormField
                                    labelKey={`${TRANSLATE_PREFIX}.DATA_SCOPE.EVENTS_LABEL`}
                                    subtitleKey={`${TRANSLATE_PREFIX}.DATA_SCOPE.EVENTS_SUBTITLE`}
                                    required={!readOnly}
                                    horizontal
                                >
                                    <Select
                                        options={eventsOptions}
                                        value={selectedEvents}
                                        onChange={opt => setGeneralField('events_data_scope', opt ? opt.value : '')}
                                        placeholder={translate(`${TAB_PREFIX}.SELECT_PLACEHOLDER`)}
                                        isSearchable={false}
                                        isClearable={false}
                                        isDisabled={readOnly}
                                        className={css.select}
                                    />
                                </FormField>
                            </>
                        )}
                    </FormInnerSection>
                </>
            )}
        </FormSection>
    );
};

GeneralTab.propTypes = {
    translate: PropTypes.func.isRequired,
    readOnly: PropTypes.bool,
};

export default withLocalize(GeneralTab);
